import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling + transient network retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Network Error: backend unreachable for a moment (hot reload, brief blip).
    // Retry once after 1.5s for safe idempotent requests.
    const isNetworkError = !error.response && error.message === 'Network Error';
    const cfg = error.config || {};
    const safeMethod = (cfg.method || 'get').toLowerCase() === 'get';
    if (isNetworkError && safeMethod && !cfg.__retried) {
      cfg.__retried = true;
      await new Promise((r) => setTimeout(r, 1500));
      try {
        return await api.request(cfg);
      } catch (retryErr) {
        return Promise.reject(retryErr);
      }
    }

    if (error.response?.status === 401) {
      // Only logout if we actually had a token (not a transient network issue)
      const token = localStorage.getItem('token');
      if (token) {
        const url = error.config?.url || '';
        // Don't log the user out for:
        //  - auth endpoints (login/register handle their own errors)
        //  - third-party integration endpoints (e.g. Pinterest). A 401 there
        //    means the *external* account/token is stale, NOT that the AJHQ
        //    session expired — logging out here is the "opening Pinterest tab
        //    logs me out" bug.
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
        const isThirdPartyIntegration = url.includes('/pinterest/');
        if (!isAuthEndpoint && !isThirdPartyIntegration) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper to add brand parameter
const withBrand = (params, brand) => {
  if (brand) {
    return { ...params, brand };
  }
  return params;
};

// API Functions
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateMoneyGoal: (moneyGoal) => api.patch('/auth/money-goal', { money_goal: moneyGoal }),
};

export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

export const inspoReelsAPI = {
  getAll: (params, brand) => api.get('/inspo-reels', { params: withBrand(params, brand) }),
  getById: (id) => api.get(`/inspo-reels/${id}`),
  create: (data, brand) => api.post('/inspo-reels', data, { params: brand ? { brand } : {} }),
  update: (id, data) => api.patch(`/inspo-reels/${id}`, data),
  delete: (id) => api.delete(`/inspo-reels/${id}`),
};

export const ideasAPI = {
  getAll: (params, brand) => api.get('/ideas', { params: withBrand(params, brand) }),
  getById: (id) => api.get(`/ideas/${id}`),
  create: (data, brand) => api.post('/ideas', data, { params: brand ? { brand } : {} }),
  update: (id, data) => api.patch(`/ideas/${id}`, data),
  greenlight: (id) => api.post(`/ideas/${id}/greenlight`),
  delete: (id) => api.delete(`/ideas/${id}`),
};

export const productionPacksAPI = {
  getAll: (params, brand) => api.get('/production-packs', { params: withBrand(params, brand) }),
  getById: (id) => api.get(`/production-packs/${id}`),
  update: (id, data) => api.patch(`/production-packs/${id}`, data),
  approve: (id) => api.post(`/production-packs/${id}/approve`),
  markCompleted: (id) => api.post(`/production-packs/${id}/complete`),
  markPosted: (id) => api.post(`/production-packs/${id}/mark-posted`),
  delete: (id) => api.delete(`/production-packs/${id}`),
  generateCaption: (id) => api.post(`/production-packs/${id}/generate-caption`, {}, { timeout: 90000 }),
  uploadFinalAsset: (packId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/production-packs/${packId}/final-assets-media`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteFinalAsset: (packId, mediaId) => api.delete(`/production-packs/${packId}/final-assets-media/${mediaId}`),
  getFinalAssetUrl: (packId, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/production-packs/${packId}/final-assets-media/${mediaId}/file?token=${token}`;
  },
  getFinalAssetDownloadUrl: (packId, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/production-packs/${packId}/final-assets-media/${mediaId}/download?token=${token}`;
  },
};

export const finalAdSetsAPI = {
  getAll: () => api.get('/ads/final-ad-sets'),
  addFromPack: (packId) => api.post(`/ads/final-ad-sets/from-pack/${packId}`),
  remove: (packId) => api.delete(`/ads/final-ad-sets/${packId}`),
  getDownloadAllUrl: (packId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/ads/final-ad-sets/${packId}/download-all?token=${token}`;
  },
};

export const productHubAPI = {
  getAll: (brand) => api.get('/products', { params: brand ? { brand } : {} }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.patch(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  uploadMedia: (id, section, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', section);
    return api.post(`/products/${id}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMedia: (id, mediaId, section) =>
    api.delete(`/products/${id}/media/${mediaId}`, { params: { section } }),
  getMediaUrl: (id, mediaId, section) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/products/${id}/media/${mediaId}/file?section=${section}&token=${token}`;
  },
  analyzeCad: (id) => api.post(`/products/${id}/analyze-cad`, {}, { timeout: 120000 }),
  sendToCustomIP: (id) => api.post(`/products/${id}/send-to-custom-ip`),
};

export const brainAPI = {
  getAgents: () => api.get('/brain/agents'),
  getAgentMemory: (agentId) => api.get(`/brain/agents/${agentId}/memory`),
  listMeetings: () => api.get('/brain/meetings'),
  createMeeting: (data) => api.post('/brain/meetings', data),
  getMeeting: (id) => api.get(`/brain/meetings/${id}`),
  updateMeeting: (id, data) => api.patch(`/brain/meetings/${id}`, data),
  deleteMeeting: (id) => api.delete(`/brain/meetings/${id}`),
  analyze: (id) => api.post(`/brain/meetings/${id}/analyze`),
  listTasks: (params) => api.get('/brain/tasks', { params }),
  updateTask: (id, data) => api.patch(`/brain/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/brain/tasks/${id}`),
  getContext: () => api.get('/brain/context'),
  updateContext: (data) => api.patch('/brain/context', data),
  getDriveStatus: () => api.get('/brain/drive/status'),
  setDriveFolder: (data) => api.post('/brain/drive/set-folder', data),
};

export const descriptionGeneratorAPI = {
  analyzeCad: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/description-generator/analyze-cad', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  analyzeUrl: (url) =>
    api.post('/description-generator/analyze-url', { url }, { timeout: 120000 }),
};

export const titleGeneratorAPI = {
  analyzeCad: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/title-generator/analyze-cad', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  analyzeUrl: (url) =>
    api.post('/title-generator/analyze-url', { url }, { timeout: 120000 }),
  analyzeText: (text) =>
    api.post('/title-generator/analyze-text', { text }, { timeout: 120000 }),
};

// ============ Etsy Hub API ============
export const workflowAPI = {
  me: () => api.get('/workflow/me'),
  summary: () => api.get('/workflow/summary'),
  items: (stages) => api.get('/workflow/items', { params: stages ? { stages } : {} }),
  item: (id) => api.get(`/workflow/items/${id}`),
  submitLink: (body) => api.post('/workflow/links', body),
  submitReview: (id, note) => api.post(`/workflow/items/${id}/submit-review`, { note }),
  requestChanges: (id, text) => api.post(`/workflow/items/${id}/request-changes`, { text }),
  approve: (id, note) => api.post(`/workflow/items/${id}/approve`, { note }),
  markLive: (id, body) => api.post(`/workflow/items/${id}/mark-live`, body),
  setChannel: (id, channel, status) => api.post(`/workflow/items/${id}/channel`, { channel, status }),
  complete: (id) => api.post(`/workflow/items/${id}/complete`),
  comment: (id, text) => api.post(`/workflow/items/${id}/comment`, { text }),
};

export const etsyAPI = {
  list: () => api.get('/etsy/products'),
  get: (id) => api.get(`/etsy/products/${id}`),
  create: (formData) =>
    api.post('/etsy/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 240000,
    }),
  retryGeneration: (id) =>
    api.post(`/etsy/products/${id}/retry-generation`, {}, { timeout: 30000 }),
  rename: (id, name) => api.patch(`/etsy/products/${id}`, { name }),
  remove: (id) => api.delete(`/etsy/products/${id}`),
  setNotForEtsy: (ids, notForEtsy) =>
    api.post('/etsy/products/not-for-etsy', { ids, not_for_etsy: notForEtsy }),
  setVisualReference: (id, link) =>
    api.patch(`/etsy/products/${id}/visual-reference`, { visual_reference_link: link }),
  regenerateTitle: (id) =>
    api.post(`/etsy/products/${id}/regenerate-title`, {}, { timeout: 180000 }),
  regenerateDescription: (id, descriptionType) =>
    api.post(
      `/etsy/products/${id}/regenerate-description`,
      descriptionType ? { description_type: descriptionType } : {},
      { timeout: 180000 }
    ),
  regenerateEbayTitle: (id) =>
    api.post(`/etsy/products/${id}/regenerate-ebay-title`, {}, { timeout: 180000 }),
  regenerateEbayDescription: (id) =>
    api.post(`/etsy/products/${id}/regenerate-ebay-description`, {}, { timeout: 180000 }),
  uploadMedia: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/etsy/products/${id}/media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  deleteMedia: (id, mediaId) => api.delete(`/etsy/products/${id}/media/${mediaId}`),
  regenerateAltText: (id, mediaId) =>
    api.post(`/etsy/products/${id}/media/${mediaId}/regenerate-alt-text`, {}, { timeout: 60000 }),
  cancelAltText: (id, mediaId) =>
    api.post(`/etsy/products/${id}/media/${mediaId}/cancel-alt-text`, {}, { timeout: 30000 }),
  editAltText: (id, mediaId, altText) =>
    api.patch(`/etsy/products/${id}/media/${mediaId}/alt-text`, { alt_text: altText }),
  editPricing: (id, payload) =>
    api.patch(`/etsy/products/${id}/pricing`, payload),
  regenerateSlug: (id) => api.post(`/etsy/products/${id}/regenerate-slug`, {}, { timeout: 60000 }),
  editSlug: (id, slug) => api.patch(`/etsy/products/${id}/slug`, { media_keyword_slug: slug }),
  // Streaming media blob endpoint (same-origin) — used for navigator.share +
  // navigator.clipboard.write so we don't hit CORS on the R2 presigned URL.
  mediaBlobUrl: (id, mediaId, { download = false } = {}) => {
    const token = localStorage.getItem('token') || '';
    const params = new URLSearchParams();
    if (download) params.set('download', '1');
    if (token) params.set('token', token);
    const qs = params.toString();
    return `${API_URL}/api/etsy/products/${id}/media/${mediaId}/blob${qs ? `?${qs}` : ''}`;
  },
  fetchMediaBlob: async (id, mediaId) => {
    const url = `/etsy/products/${id}/media/${mediaId}/blob`;
    const res = await api.get(url, { responseType: 'blob', timeout: 180000 });
    return res.data;
  },
};

// ============ Video Bank API ============
export const videoBankAPI = {
  list: (params) => api.get('/video-bank', { params }),
  remove: (id) => api.delete(`/video-bank/${id}`),
};

// ============ Incentives / KPI System API ============
export const incentivesAPI = {
  settings: () => api.get('/incentives/settings'),
  updateSettings: (data) => api.patch('/incentives/settings', data),
  periods: () => api.get('/incentives/periods'),
  currentPeriod: () => api.get('/incentives/period/current'),
  createPeriod: (data) => api.post('/incentives/periods', data),
  updatePeriod: (id, data) => api.patch(`/incentives/periods/${id}`, data),
  employees: () => api.get('/incentives/employees'),
  me: (periodId) => api.get('/incentives/me', { params: periodId ? { period_id: periodId } : {} }),
  employee: (id, periodId) => api.get(`/incentives/employee/${id}`, { params: periodId ? { period_id: periodId } : {} }),
  board: (periodId) => api.get('/incentives/board', { params: periodId ? { period_id: periodId } : {} }),
  history: (employeeId, periodId) => api.get('/incentives/history', {
    params: { ...(employeeId ? { employee_id: employeeId } : {}), ...(periodId ? { period_id: periodId } : {}) },
  }),
  dailyGrid: (targetDate) => api.get('/incentives/daily', { params: targetDate ? { target_date: targetDate } : {} }),
  saveDaily: (data) => api.post('/incentives/daily', data),
  closeDay: (date) => api.post('/incentives/daily/close-day', { date }),
  reopenDay: (date, reason) => api.post('/incentives/daily/reopen-day', { date, reason }),
  editMetric: (employeeId, data) => api.patch(`/incentives/employee/${employeeId}/metric`, data),
  addLeave: (data) => api.post('/incentives/leave', data),
  removeLeave: (id) => api.delete(`/incentives/leave/${id}`),
  listLeave: (employeeId) => api.get('/incentives/leave', { params: employeeId ? { employee_id: employeeId } : {} }),
  addHoliday: (data) => api.post('/incentives/calendar/holiday', data),
  removeHoliday: (date) => api.delete(`/incentives/calendar/holiday/${date}`),
  calendar: () => api.get('/incentives/calendar'),
  improvements: (employeeId) => api.get('/incentives/improvements', { params: employeeId ? { employee_id: employeeId } : {} }),
  submitImprovement: (data) => api.post('/incentives/improvements', data),
  reviewImprovement: (id, data) => api.patch(`/incentives/improvements/${id}`, data),
  addIncident: (data) => api.post('/incentives/incidents', data),
  audit: () => api.get('/incentives/audit'),
  access: () => api.get('/incentives/access'),
  addAdmin: (email) => api.post('/incentives/admins', { email }),
  removeAdmin: (email) => api.delete(`/incentives/admins/${encodeURIComponent(email)}`),
};

// ============ Reseller pack API ============
export const walmartAPI = {
  status: () => api.get('/walmart/status'),
  get: (productId) => api.get(`/walmart/products/${productId}`),
  patch: (productId, fields) => api.patch(`/walmart/products/${productId}`, fields),
  preview: (productId, overrides = {}) => api.post(`/walmart/products/${productId}/preview`, overrides, { timeout: 30000 }),
  publish: (productId, overrides = {}) => api.post(`/walmart/products/${productId}/publish`, overrides, { timeout: 90000 }),
  refreshStatus: (productId) => api.post(`/walmart/products/${productId}/refresh-status`, {}, { timeout: 30000 }),
  generateCopy: (productId, body = {}) => api.post(`/walmart/products/${productId}/generate-copy`, body, { timeout: 180000 }),
  exportSheetPreflight: (body = {}) =>
    api.post('/walmart/export-sheet', { ...body, validate_only: true }, { timeout: 120000 }),
  exportSheet: (body = {}) =>
    api.post('/walmart/export-sheet', body, { responseType: 'blob', timeout: 180000 }),
};

export const amazonAPI = {
  status: () => api.get('/amazon/status'),
  get: (productId) => api.get(`/amazon/products/${productId}`),
  patch: (productId, fields) => api.patch(`/amazon/products/${productId}`, fields),
  generate: (productId) => api.post(`/amazon/products/${productId}/generate`, {}, { timeout: 30000 }),
  variations: (productId) => api.get(`/amazon/products/${productId}/variations`),
  preview: (productId, overrides = {}) => api.post(`/amazon/products/${productId}/preview`, overrides, { timeout: 30000 }),
  validate: (productId, overrides = {}) => api.post(`/amazon/products/${productId}/validate`, overrides, { timeout: 60000 }),
  publish: (productId, overrides = {}) => api.post(`/amazon/products/${productId}/publish`, overrides, { timeout: 90000 }),
};

export const resellerAPI = {
  get: (productId) => api.get(`/etsy/products/${productId}/reseller`),
  generateDescription: (productId) =>
    api.post(`/etsy/products/${productId}/reseller/generate-description`, {}, { timeout: 120000 }),
  editDescription: (productId, description) =>
    api.patch(`/etsy/products/${productId}/reseller/description`, { description }),
  generateHero: (productId, sourceMediaId) =>
    api.post(`/etsy/products/${productId}/reseller/media/generate-hero`, { source_media_id: sourceMediaId }, { timeout: 30000 }),
  generateContinuation: (productId, sourceMediaId, parentMediaId = null) =>
    api.post(
      `/etsy/products/${productId}/reseller/media/generate-continuation`,
      { source_media_id: sourceMediaId, parent_media_id: parentMediaId },
      { timeout: 30000 },
    ),
  deleteMedia: (productId, rmId) =>
    api.delete(`/etsy/products/${productId}/reseller/media/${rmId}`),
  mediaBlobUrl: (productId, rmId, { download = false } = {}) => {
    const token = localStorage.getItem('token') || '';
    const params = new URLSearchParams();
    if (download) params.set('download', '1');
    if (token) params.set('token', token);
    return `${API_URL}/api/etsy/products/${productId}/reseller/media/${rmId}/blob?${params.toString()}`;
  },
  fetchMediaBlob: async (productId, rmId) => {
    const url = `/etsy/products/${productId}/reseller/media/${rmId}/blob`;
    const params = { token: localStorage.getItem('token') || '' };
    const res = await api.get(url, { params, responseType: 'blob', timeout: 180000 });
    return res.data;
  },
};

// ============ Etsy Draft Publisher API ============
export const etsyListingAPI = {
  oauthStart: () => api.get('/etsy-auth/start'),
  oauthStatus: () => api.get('/etsy-auth/status'),
  oauthDisconnect: () => api.post('/etsy-auth/disconnect'),
  shopDefaults: () => api.get('/etsy/shop/defaults', { timeout: 90000 }),
  generateTags: (id) => api.post(`/etsy/products/${id}/generate-tags`, {}, { timeout: 120000 }),
  getAttributes: (id) => api.get(`/etsy/products/${id}/attributes`, { timeout: 90000 }),
  pushDraft: (id, payload) => api.post(`/etsy/products/${id}/push-draft`, payload, { timeout: 30000 }),
  pushPricing: (id, payload = {}) => api.post(`/etsy/products/${id}/push-pricing`, payload, { timeout: 30000 }),
  pushMedia: (id, payload = {}) => api.post(`/etsy/products/${id}/push-media`, payload, { timeout: 30000 }),
  linkListing: (id, listing) => api.post(`/etsy/products/${id}/link-listing`, { listing }, { timeout: 30000 }),
  pushStatus: (id) => api.get(`/etsy/products/${id}/push-status`),
};

// ============ Ad Creatives ============
export const adCreativesAPI = {
  listListings: (q = '') => api.get(`/ad-creatives/listings${q ? `?q=${encodeURIComponent(q)}` : ''}`, { timeout: 60000 }),
  listSets: () => api.get('/ad-creatives/sets'),
  createSet: (payload) => api.post('/ad-creatives/sets', payload),
  getSet: (id) => api.get(`/ad-creatives/sets/${id}`),
  renameSet: (id, name) => api.patch(`/ad-creatives/sets/${id}`, { name }),
  deleteSet: (id) => api.delete(`/ad-creatives/sets/${id}`),
  deleteMedia: (id, mid) => api.delete(`/ad-creatives/sets/${id}/media/${mid}`),
  editMedia: (id, mid, payload) => api.patch(`/ad-creatives/sets/${id}/media/${mid}`, payload),
  generateTitle: (id, mid) => api.post(`/ad-creatives/sets/${id}/media/${mid}/generate-title`, {}, { timeout: 130000 }),
  generateAllTitles: (id) => api.post(`/ad-creatives/sets/${id}/generate-all-titles`, {}, { timeout: 30000 }),
};

export const agentApiAPI = {
  getKey: () => api.get('/agent/v1/key'),
  ping: (key) => api.get('/agent/v1/ping', { headers: { 'X-API-Key': key } }),
};

export const storySetsAPI = {
  listListings: (q = '') => api.get(`/story-sets/listings${q ? `?q=${encodeURIComponent(q)}` : ''}`, { timeout: 60000 }),
  listSets: () => api.get('/story-sets/sets'),
  createSet: (payload) => api.post('/story-sets/sets', payload),
  getSet: (id) => api.get(`/story-sets/sets/${id}`),
  renameSet: (id, name) => api.patch(`/story-sets/sets/${id}`, { name }),
  deleteSet: (id) => api.delete(`/story-sets/sets/${id}`),
  deleteMedia: (id, mid) => api.delete(`/story-sets/sets/${id}/media/${mid}`),
  getMediaFileBlob: (id, mid) => api.get(`/story-sets/sets/${id}/media/${mid}/file`, { responseType: 'blob', timeout: 120000 }),
  downloadZip: (id) => api.get(`/story-sets/sets/${id}/download`, { responseType: 'blob', timeout: 300000 }),
};

// ============ Appraisal Certificate Generator ============
export const appraisalAPI = {
  goldPrice: () => api.get('/appraisal/gold-price'),
  get: (id) => api.get(`/appraisal/products/${id}`),
  update: (id, payload) => api.put(`/appraisal/products/${id}`, payload),
  generate: (id, fmt = 'both', imageUrls) => api.post(
    `/appraisal/products/${id}/generate`,
    { image_urls: imageUrls || [] },
    { params: { fmt }, timeout: 120000 },
  ),
};

// ============ eBay Listing API ============
export const ebayAPI = {
  oauthStart: (envName) => api.get('/ebay/oauth/start', { params: envName ? { env_name: envName } : {} }),
  oauthStatus: () => api.get('/ebay/oauth/status'),
  oauthDisconnect: () => api.post('/ebay/oauth/disconnect'),
  getPolicies: () => api.get('/ebay/policies'),
  getLocations: () => api.get('/ebay/locations'),
  createLocation: (payload) => api.post('/ebay/locations', payload),
  categorySuggestions: (q) => api.get('/ebay/category-suggestions', { params: { q } }),
  saveDraft: (productId, payload) =>
    api.post(`/ebay/products/${productId}/save-draft`, payload, { timeout: 60000 }),
  saveDraftWithVariations: (productId, payload) =>
    api.post(`/ebay/products/${productId}/save-draft-with-variations`, payload, { timeout: 600000 }),
  saveDraftFixed: (productId, payload) =>
    api.post(`/ebay/products/${productId}/save-draft-fixed`, payload, { timeout: 120000 }),
  extractFixed: (productId) => api.get(`/ebay/products/${productId}/extract-fixed`),
  draftStatus: (productId) => api.get(`/ebay/products/${productId}/draft-status`, { timeout: 30000 }),
  publish: (productId) => api.post(`/ebay/products/${productId}/publish`, {}, { timeout: 120000 }),
  // ----- Chat editor -----
  getChat: (productId) => api.get(`/ebay/products/${productId}/chat`),
  sendChat: (productId, message) =>
    api.post(`/ebay/products/${productId}/chat`, { message }, { timeout: 90000 }),
  applyChange: (productId, messageId, change) =>
    api.post(`/ebay/products/${productId}/apply-change`, { message_id: messageId, change }, { timeout: 600000 }),
  clearChat: (productId) => api.delete(`/ebay/products/${productId}/chat`),
  // HTML description
  generateHtmlDescription: (productId, regenerate = false) =>
    api.post(`/ebay/products/${productId}/generate-html-description`, { regenerate }, { timeout: 90000 }),
  deleteHtmlDescription: (productId) => api.delete(`/ebay/products/${productId}/html-description`),
};

// ============ The Local Jewel — Projects API ============
export const tljAPI = {
  preview: (productId) => api.get(`/tlj/products/${productId}/preview`),
  generateSeo: (productId, body = {}) =>
    api.post(`/tlj/products/${productId}/generate-seo`, body, { timeout: 90000 }),
  generateFaqs: (productId) =>
    api.post(`/tlj/products/${productId}/generate-faqs`, {}, { timeout: 120000 }),
  generateSeoPhrases: (productId) =>
    api.post(`/tlj/products/${productId}/generate-seo-phrases`, {}, { timeout: 120000 }),
  editSeo: (productId, body) => api.patch(`/tlj/products/${productId}/seo`, body),
  editExtras: (productId, body) => api.patch(`/tlj/products/${productId}/extras`, body),
  publish: (productId) =>
    api.post(`/tlj/products/${productId}/publish`, {}, { timeout: 30000 }),
  publishStatus: (productId) => api.get(`/tlj/products/${productId}/publish-status`),
  unpublish: (productId) => api.post(`/tlj/products/${productId}/unpublish`),
  priceMatrix: (productId) => api.get(`/tlj/products/${productId}/price-matrix`),
  pushPricing: (productId) =>
    api.post(`/tlj/products/${productId}/push-pricing`, {}, { timeout: 60000 }),
};

// ============ Media Generator API ============
export const mediaGenAPI = {
  generate: (payload) => api.post('/media-generator/generate', payload, { timeout: 60000 }),
  uploadReference: (file, kind = 'image') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    return api.post('/media-generator/upload-reference', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  list: (params = {}) => api.get('/media-generator/jobs', { params }),
  get: (id) => api.get(`/media-generator/jobs/${id}`),
  refresh: (id) => api.post(`/media-generator/jobs/${id}/refresh`),
  cancel: (id) => api.post(`/media-generator/jobs/${id}/cancel`),
  delete: (id) => api.delete(`/media-generator/jobs/${id}`),
};

export const finalAssetsAPI = {
  getAll: (params, brand) => api.get('/final-assets', { params: withBrand(params, brand) }),
  getById: (id) => api.get(`/final-assets/${id}`),
  upload: (formData, brand) => api.post('/final-assets/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: brand ? { brand } : {},
  }),
  create: (data, brand) => api.post('/final-assets', data, { params: brand ? { brand } : {} }),
  update: (id, data) => api.patch(`/final-assets/${id}`, data),
  markPosted: (id) => api.post(`/final-assets/${id}/mark-posted`),
  delete: (id) => api.delete(`/final-assets/${id}`),
};

export const brandLibraryAPI = {
  getAll: (params, brand) => api.get('/brand-library', { params: withBrand(params, brand) }),
  create: (data, brand) => api.post('/brand-library', data, { params: brand ? { brand } : {} }),
  update: (id, data) => api.patch(`/brand-library/${id}`, data),
  delete: (id) => api.delete(`/brand-library/${id}`),
};

export const dashboardAPI = {
  getStats: (brand) => api.get('/dashboard/stats', { params: brand ? { brand } : {} }),
};

export const calendarAPI = {
  getData: (params, brand) => api.get('/calendar', { params: withBrand(params, brand) }),
  schedule: (assetId, postingDate) => api.patch(`/calendar/schedule/${assetId}`, { posting_date: postingDate }),
  unschedule: (assetId) => api.patch(`/calendar/unschedule/${assetId}`),
};

export const todosAPI = {
  getCategories: () => api.get('/todos/categories'),
  getUsers: () => api.get('/todos/users'),
  getAll: (params) => api.get('/todos', { params }),
  getById: (id) => api.get(`/todos/${id}`),
  create: (data) => api.post('/todos', data),
  update: (id, data) => api.patch(`/todos/${id}`, data),
  toggle: (id) => api.post(`/todos/${id}/toggle`),
  delete: (id) => api.delete(`/todos/${id}`),
};

export const teamMembersAPI = {
  getAll: (brand) => api.get('/team-members', { params: brand ? { brand } : {} }),
  create: (data) => api.post('/team-members', data),
  delete: (id) => api.delete(`/team-members/${id}`),
};

export const notesAPI = {
  getCategories: () => api.get('/notes/categories'),
  getAll: (category, brand) => api.get('/notes', { params: { ...(category ? { category } : {}), ...(brand ? { brand } : {}) } }),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.patch(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
};

export const inventoryAPI = {
  getOptions: () => api.get('/inventory/options'),
  getAll: (itemType) => api.get('/inventory', { params: itemType ? { item_type: itemType } : {} }),
  getTotalValue: () => api.get('/inventory/total-value'),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.patch(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
};

export const uploadAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const productionAudioAPI = {
  upload: (packId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/production-packs/${packId}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (packId, audioId) => api.delete(`/production-packs/${packId}/audio/${audioId}`),
  getDownloadUrl: (packId, audioId) => `${API_URL}/api/production-packs/${packId}/audio/${audioId}/download`,
  getStreamUrl: (packId, audioId) => `${API_URL}/api/production-packs/${packId}/audio/${audioId}/stream`,
};

export const productionVisualsAPI = {
  upload: (packId, file, visualType = 'raw') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visual_type', visualType);
    return api.post(`/production-packs/${packId}/visuals`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (packId, visualId) => api.delete(`/production-packs/${packId}/visuals/${visualId}`),
  getFileUrl: (packId, visualId) => `${API_URL}/api/production-packs/${packId}/visuals/${visualId}/file`,
  getDownloadUrl: (packId, visualId) => `${API_URL}/api/production-packs/${packId}/visuals/${visualId}/download`,
};

export const overviewVoiceAPI = {
  upload: (packId, audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'voice_note.webm');
    return api.post(`/production-packs/${packId}/overview-voice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (packId) => api.delete(`/production-packs/${packId}/overview-voice`),
  getStreamUrl: (packId) => `${API_URL}/api/production-packs/${packId}/overview-voice/stream`,
};

export const editorNotesAPI = {
  // Instagram references
  addInstagram: (packId, url) => api.post(`/production-packs/${packId}/editor/instagram`, { url }),
  deleteInstagram: (packId, refId) => api.delete(`/production-packs/${packId}/editor/instagram/${refId}`),
  
  // Voice notes
  uploadVoice: (packId, audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'editor_voice.webm');
    return api.post(`/production-packs/${packId}/editor/voice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteVoice: (packId, voiceId) => api.delete(`/production-packs/${packId}/editor/voice/${voiceId}`),
  getVoiceStreamUrl: (packId, voiceId) => `${API_URL}/api/production-packs/${packId}/editor/voice/${voiceId}/stream`,
};


export const productPacksAPI = {
  getAll: (brand) => api.get("/product-packs", { params: brand ? { brand } : {} }),
  getById: (id) => api.get(`/product-packs/${id}`),
  create: (data) => api.post("/product-packs", data),
  update: (id, data) => api.patch(`/product-packs/${id}`, data),
  delete: (id) => api.delete(`/product-packs/${id}`),
  uploadMedia: (packId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/product-packs/${packId}/media`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteMedia: (packId, mediaId) => api.delete(`/product-packs/${packId}/media/${mediaId}`),
  downloadAll: (packId) => api.get(`/product-packs/${packId}/download-all`),
  shareToResellers: (packId, resellerIds) => api.post(`/product-packs/${packId}/share-resellers`, { reseller_ids: resellerIds }),
};



export const customIPPacksAPI = {
  getAll: (brand) => api.get("/custom-ip-packs", { params: brand ? { brand } : {} }),
  getById: (id) => api.get(`/custom-ip-packs/${id}`),
  create: (data) => api.post("/custom-ip-packs", data),
  update: (id, data) => api.patch(`/custom-ip-packs/${id}`, data),
  delete: (id) => api.delete(`/custom-ip-packs/${id}`),
  uploadMedia: (packId, section, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/custom-ip-packs/${packId}/media/${section}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteMedia: (packId, section, mediaId) => api.delete(`/custom-ip-packs/${packId}/media/${section}/${mediaId}`),
  getMediaUrl: (packId, section, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/custom-ip-packs/${packId}/media/${section}/${mediaId}/file?token=${token}`;
  },
  getMediaDownloadUrl: (packId, section, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/custom-ip-packs/${packId}/media/${section}/${mediaId}/file?download=1&token=${token}`;
  },
  addTextReview: (packId, text) => {
    const formData = new FormData();
    formData.append("review_text", text);
    return api.post(`/custom-ip-packs/${packId}/reviews/text`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteTextReview: (packId, reviewId) => api.delete(`/custom-ip-packs/${packId}/reviews/text/${reviewId}`),
  // Voice notes
  uploadVoiceNote: (packId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/custom-ip-packs/${packId}/voice-notes`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteVoiceNote: (packId, noteId) => api.delete(`/custom-ip-packs/${packId}/voice-notes/${noteId}`),
  getVoiceNoteStreamUrl: (packId, noteId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/custom-ip-packs/${packId}/voice-notes/${noteId}/stream?token=${token}`;
  },
  submitForApproval: (packId) => api.post(`/custom-ip-packs/${packId}/submit-for-approval`),
  approve: (packId) => api.post(`/custom-ip-packs/${packId}/approve`),
  addReferenceReel: (packId, url) => api.post(`/custom-ip-packs/${packId}/reference-reels`, { url }),
  deleteReferenceReel: (packId, refId) => api.delete(`/custom-ip-packs/${packId}/reference-reels/${refId}`),
  generateScript: (packId, influencerId) => api.post(`/custom-ip-packs/${packId}/generate-script`, { influencer_id: influencerId }, { timeout: 120000 }),
};

export const celebrityIPPacksAPI = {
  getAll: (brand) => api.get("/celebrity-ip-packs", { params: brand ? { brand } : {} }),
  getById: (id) => api.get(`/celebrity-ip-packs/${id}`),
  create: (data) => api.post("/celebrity-ip-packs", data),
  update: (id, data) => api.patch(`/celebrity-ip-packs/${id}`, data),
  delete: (id) => api.delete(`/celebrity-ip-packs/${id}`),
  uploadMedia: (packId, section, file) => {
    const formData = new FormData();
    formData.append("files", file);
    return api.post(`/celebrity-ip-packs/${packId}/media/${section}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteMedia: (packId, section, mediaId) => api.delete(`/celebrity-ip-packs/${packId}/media/${section}/${mediaId}`),
  getMediaUrl: (packId, section, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/celebrity-ip-packs/${packId}/media/${section}/${mediaId}/file?token=${token}`;
  },
  addTextReview: (packId, text) => {
    const formData = new FormData();
    formData.append("review_text", text);
    return api.post(`/celebrity-ip-packs/${packId}/reviews/text`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteTextReview: (packId, reviewId) => api.delete(`/celebrity-ip-packs/${packId}/reviews/text/${reviewId}`),
  submitForApproval: (packId) => api.post(`/celebrity-ip-packs/${packId}/submit-for-approval`),
  approve: (packId) => api.post(`/celebrity-ip-packs/${packId}/approve`),
  addReferenceReel: (packId, url) => api.post(`/celebrity-ip-packs/${packId}/reference-reels`, { url }),
  deleteReferenceReel: (packId, refId) => api.delete(`/celebrity-ip-packs/${packId}/reference-reels/${refId}`),
};

export const redditLeadsAPI = {
  getAll: (params) => api.get('/reddit-leads', { params }),
  getStats: () => api.get('/reddit-leads/stats'),
  getById: (id) => api.get(`/reddit-leads/${id}`),
  update: (id, data) => api.patch(`/reddit-leads/${id}`, data),
  delete: (id) => api.delete(`/reddit-leads/${id}`),
};


export const crmAPI = {
  // Contacts
  getContacts: (params) => api.get("/crm/contacts", { params }),
  getContact: (id) => api.get(`/crm/contacts/${id}`),
  createContact: (data) => api.post("/crm/contacts", data),
  updateContact: (id, data) => api.patch(`/crm/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/crm/contacts/${id}`),
  markContacted: (id) => api.post(`/crm/contacts/${id}/mark-contacted`),
  
  // Bulk operations
  importCSV: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/crm/contacts/import-csv", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  bulkDelete: (contactIds) => api.delete("/crm/contacts/bulk/delete", { data: contactIds }),
  bulkMarkContacted: (contactIds) => api.post("/crm/contacts/bulk/mark-contacted", contactIds),
  
  // Messaging
  sendBulkMessage: (data) => api.post("/crm/send-bulk-message", data),
  getMessagingStatus: () => api.get("/crm/messaging/status"),
  getWhatsAppTemplates: () => api.get("/crm/whatsapp/templates"),
  
  // API Key
  getAPIKey: () => api.get("/crm/api-key"),
  regenerateAPIKey: () => api.post("/crm/api-key/regenerate"),

  // Send Queue
  createSendQueue: (data) => api.post("/crm/send-queue", data),
  getSendQueue: (id) => api.get(`/crm/send-queue/${id}`),
  updateQueueItemStatus: (queueId, itemId, status) =>
    api.patch(`/crm/send-queue/${queueId}/items/${itemId}/status`, { status }),
  sendQueueBulk: (queueId) => api.post(`/crm/send-queue/${queueId}/send-bulk`),

  // Message Templates
  getMessageTemplates: () => api.get("/crm/message-templates"),
  createMessageTemplate: (data) => api.post("/crm/message-templates", data),
  deleteMessageTemplate: (id) => api.delete(`/crm/message-templates/${id}`),
};

// ============ Platforms API ============
export const platformsAPI = {
  getCatalog: () => api.get("/platforms/catalog"),
  getLinks: () => api.get("/platforms"),
  saveLink: (data) => api.post("/platforms", data),
  deleteLink: (key) => api.delete(`/platforms/${key}`),
};


// ============ Story Generator API ============
export const storyGeneratorAPI = {
  generate: (brief) => api.post("/story-generator/generate", { brief }),
  getHistory: () => api.get("/story-generator/history"),
  delete: (id) => api.delete(`/story-generator/${id}`),
  downloadUrl: (id) => `${API_URL}/api/story-generator/${id}/download`,
};


// ============ Research API ============
export const researchAPI = {
  listInfluencers: () => api.get("/research/influencers"),
  createInfluencer: (data) => api.post("/research/influencers", data),
  getInfluencer: (id) => api.get(`/research/influencers/${id}`),
  deleteInfluencer: (id) => api.delete(`/research/influencers/${id}`),
  uploadVideo: (id, formData) => api.post(`/research/influencers/${id}/videos`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  deleteVideo: (id, vidId) => api.delete(`/research/influencers/${id}/videos/${vidId}`),
  analyze: (id) => api.post(`/research/influencers/${id}/analyze`, {}, { timeout: 300000 }),
  convertScript: (id, script) => api.post(`/research/influencers/${id}/convert-script`, { script }),
  generateScript: (id, formData) => api.post(`/research/influencers/${id}/generate-script`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }),
};


// ============ E2E Cloner API ============
export const e2eClonerAPI = {
  listProjects: () => api.get("/e2e-cloner/projects"),
  createProject: (data) => api.post("/e2e-cloner/projects", data),
  getProject: (id) => api.get(`/e2e-cloner/projects/${id}`),
  deleteProject: (id) => api.delete(`/e2e-cloner/projects/${id}`),
  uploadVideo: (id, formData) => api.post(`/e2e-cloner/projects/${id}/videos`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  deleteVideo: (id, vidId) => api.delete(`/e2e-cloner/projects/${id}/videos/${vidId}`),
  analyze: (id) => api.post(`/e2e-cloner/projects/${id}/analyze`, {}),
  reset: (id) => api.post(`/e2e-cloner/projects/${id}/reset`, {}),
  reanalyzeVideo: (id, vidId) => api.post(`/e2e-cloner/projects/${id}/videos/${vidId}/reanalyze`, {}),
};


// ============ Mood Board Generator (standalone) ============
export const moodBoardAPI = {
  uploadReference: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/mood-board/upload-reference', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  generate: (payload) => api.post('/mood-board/generate', payload, { timeout: 60000 }),
  getOne: (id) => api.get(`/mood-board/${id}`),
  history: () => api.get('/mood-board/history'),
  delete: (id) => api.delete(`/mood-board/${id}`),
  etsySources: () => api.get('/mood-board/etsy-source-list'),
};

// ============ 3D Render Generator (standalone) ============
export const render3dAPI = {
  uploadReference: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/render-3d/upload-reference', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  generate: (payload) => api.post('/render-3d/generate', payload, { timeout: 60000 }),
  getOne: (id) => api.get(`/render-3d/${id}`),
  history: () => api.get('/render-3d/history'),
  delete: (id) => api.delete(`/render-3d/${id}`),
};

// ============ Olivia (HeyGen talking-photo videos) ============
export const oliviaAPI = {
  getAvatar: () => api.get('/olivia/avatar'),
  create: (payload) => api.post('/olivia/videos', payload),
  list: () => api.get('/olivia/videos'),
  getOne: (id) => api.get(`/olivia/videos/${id}`),
  delete: (id) => api.delete(`/olivia/videos/${id}`),
};

// ============ TLJ Project Story API ============
export const tljProjectStoryAPI = {
  get: (productId) => api.get(`/tlj/products/${productId}/project-story`),
  patchBaseStory: (productId, data) =>
    api.patch(`/tlj/products/${productId}/project-story/base-story`, data),
  uploadBaseStoryImage: (productId, kind, dataUrl) =>
    api.post(`/tlj/products/${productId}/project-story/base-story/upload-image`, { kind, data_url: dataUrl }),
  brainstormChanges: (productId, changeHint) =>
    api.post(`/tlj/products/${productId}/project-story/base-story/brainstorm-changes`, { change_hint: changeHint }, { timeout: 60000 }),
  generateBaseStoryParagraph: (productId, body) =>
    api.post(`/tlj/products/${productId}/project-story/base-story/generate-paragraph`, body, { timeout: 180000 }),
  generateStep: (productId, stepKey, hint) =>
    api.post(`/tlj/products/${productId}/project-story/steps/${stepKey}/generate`, { hint }, { timeout: 180000 }),
  patchStep: (productId, stepKey, data) =>
    api.patch(`/tlj/products/${productId}/project-story/steps/${stepKey}`, data),
  generateImessage: (productId, body) =>
    api.post(`/tlj/products/${productId}/project-story/steps/client_brief/imessage`, body, { timeout: 60000 }),
  attach3dRender: (productId, renderId) =>
    api.post(`/tlj/products/${productId}/project-story/steps/renders_3d/attach`, { render_3d_gen_id: renderId }),
  generateInlineMoodboard: (productId, body) =>
    api.post(`/tlj/products/${productId}/project-story/base-story/generate-moodboard`, body, { timeout: 60000 }),
  generateInlineRender: (productId, body) =>
    api.post(`/tlj/products/${productId}/project-story/steps/renders_3d/build`, body, { timeout: 60000 }),
  generateSeo: (productId) =>
    api.post(`/tlj/products/${productId}/project-story/seo/generate`, {}, { timeout: 180000 }),
  patchSeo: (productId, data) =>
    api.patch(`/tlj/products/${productId}/project-story/seo`, data),
  patchClientName: (productId, clientName) =>
    api.patch(`/tlj/products/${productId}/project-story/client-name`, { client_name: clientName }),
  generateTestimonial: (productId, clientName) =>
    api.post(
      `/tlj/products/${productId}/project-story/testimonial/generate`,
      { client_name: clientName || '' },
      { timeout: 180000 }
    ),
  patchTestimonial: (productId, testimonial) =>
    api.patch(`/tlj/products/${productId}/project-story/testimonial`, { testimonial }),
};

// ============ Pinterest API ============
export const pinterestAPI = {
  oauthStatus: () => api.get('/pinterest/oauth/status'),
  oauthStart: () => api.get('/pinterest/oauth/start'),
  oauthDisconnect: () => api.post('/pinterest/oauth/disconnect'),
  listBoards: () => api.get('/pinterest/boards'),
  createBoard: (data) => api.post('/pinterest/boards', data),
  listProductPins: (productId) => api.get(`/pinterest/etsy/${productId}/pins`),
  generatePin: (productId, pinType, payload) =>
    api.post(`/pinterest/etsy/${productId}/pins/${pinType}/generate`, payload, { timeout: 240000 }),
  generateAllPins: (productId, selections) =>
    api.post(`/pinterest/etsy/${productId}/pins/generate-all`, { selections }, { timeout: 480000 }),
  patchPin: (productId, pinType, data) =>
    api.patch(`/pinterest/etsy/${productId}/pins/${pinType}`, data),
  deletePin: (productId, pinType) =>
    api.delete(`/pinterest/etsy/${productId}/pins/${pinType}`),
  generateMoodBoardImage: (productId, payload) =>
    api.post(`/pinterest/etsy/${productId}/pins/mood_board/generate-image`, payload, { timeout: 180000 }),
  publishPin: (productId, pinType, payload) =>
    api.post(`/pinterest/etsy/${productId}/pins/${pinType}/publish`, payload, { timeout: 90000 }),
  pinAnalytics: (productId, pinType, days = 30) =>
    api.get(`/pinterest/etsy/${productId}/pins/${pinType}/analytics`, { params: { days } }),
};

// ============ Reddit Posts API ============
export const redditPostsAPI = {
  getAll: (brand) => api.get("/reddit-posts", { params: brand ? { brand } : {} }),
  getById: (id) => api.get(`/reddit-posts/${id}`),
  create: (title) => {
    const formData = new FormData();
    formData.append("title", title);
    return api.post("/reddit-posts", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  update: (id, data) => {
    const formData = new FormData();
    if (data.title !== undefined) formData.append("title", data.title);
    if (data.post_copy !== undefined) formData.append("post_copy", data.post_copy);
    return api.patch(`/reddit-posts/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  delete: (id) => api.delete(`/reddit-posts/${id}`),
  uploadMedia: (postId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/reddit-posts/${postId}/media`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteMedia: (postId, mediaId) => api.delete(`/reddit-posts/${postId}/media/${mediaId}`),
  getMediaUrl: (postId, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/reddit-posts/${postId}/media/${mediaId}/file?token=${token}`;
  },
  getMediaDownloadUrl: (postId, mediaId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/reddit-posts/${postId}/media/${mediaId}/download?token=${token}`;
  },
  uploadVoiceNote: (postId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/reddit-posts/${postId}/voice-notes`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteVoiceNote: (postId, noteId) => api.delete(`/reddit-posts/${postId}/voice-notes/${noteId}`),
  getVoiceNoteStreamUrl: (postId, noteId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/reddit-posts/${postId}/voice-notes/${noteId}/stream?token=${token}`;
  },
};
